import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import TextField from '@shared/ui/atoms/TextField';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import {
  DropDownListItem,
  ReactSelectDropDownList
} from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { createLocker, selectIsLockersSaving, updateLocker } from '../data/lockers.duck';
import { Locker } from '../data/lockers.models';

/**
 * Adding or renaming a locker is a short, self-contained job, so it takes over the screen for a
 * moment rather than pushing the table down the page while an admin fills in two fields.
 */
export function LockerDialog(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allOffices = useSelector(selectAllOfficeEntities);
  const isSaving = useSelector(selectIsLockersSaving);

  const isEditing = !!props.locker;
  const [label, setLabel] = useState(props.locker?.label ?? '');
  const [officeId, setOfficeId] = useState<number>(props.locker?.officeId ?? props.rootOfficeId);

  // A locker can sit in the office itself or on one of its floors, but never in an area.
  const placeableOffices: DropDownListItem[] = useMemo(() => {
    return allOffices
      .filter((office: OfficeEntity) =>
        !office.area && (office.id === props.rootOfficeId || office.parentId === props.rootOfficeId))
      .map((office: OfficeEntity) => ({ value: office.id, label: office.label }));
  }, [allOffices, props.rootOfficeId]);

  const selectedOffice = useMemo(() => {
    return placeableOffices.find((option: DropDownListItem) => option.value === officeId);
  }, [placeableOffices, officeId]);

  const onSave = () => {
    if (!label.trim()) return;

    if (props.locker) {
      // active is sent as it stands: this dialog renames and moves a locker, it does not retire one.
      dispatch(updateLocker({
        lockerId: props.locker.id,
        request: { officeId: Number(officeId), label: label.trim(), active: props.locker.active },
      }));
    } else {
      dispatch(createLocker({ officeId: Number(officeId), label: label.trim() }));
    }

    setLabel('');
    props.onClose();
  };

  return (
    <Dialog isOpen={true} onClose={props.onClose}>
      <DialogHeader title={isEditing ? 'lockers.edit-locker' : 'lockers.add-locker'} />

      <DialogForm>
        <TextField label={'lockers.label'} value={label} onChange={setLabel}
                   placeholder={'lockers.label-placeholder'} style={{ margin: 0 }} />

        <LocationSelectWrapper>
          <BodySmall colour={Colours.darkGrey}>{t('lockers.location')}</BodySmall>
          <ReactSelectDropDownList items={placeableOffices}
                                   selectedItem={selectedOffice}
                                   placeholder={'lockers.select-location'}
                                   portalMenu={true}
                                   onItemSelected={(item: DropDownListItem) => setOfficeId(Number(item.value))} />
        </LocationSelectWrapper>

        {!isEditing && (
          <BulkLockersHint colour={Colours.darkGrey}>
            <Trans i18nKey={'lockers.bulk-add-help'}
                   components={[<a href={'mailto:hello@team-today.com'}>hello@team-today.com</a>]} />
          </BulkLockersHint>
        )}

        <DialogActions>
          <PrimaryButton text={'button.save'} disabled={isSaving || !label.trim()} click={onSave} />
          <OutlineButton text={'button.cancel'} disabled={isSaving} click={props.onClose} />
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

interface Props {
  rootOfficeId: number;
  /** Present when renaming or moving an existing locker, absent when adding one. */
  locker?: Locker;
  onClose: () => void;
}

// Enough room that the dialog does not resize around the dropdown as it opens and closes.
const DialogForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  min-height: 320px;
`

// react-select sizes to its container, so it needs a width of its own inside the column
const LocationSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
`

const BulkLockersHint = styled(BodySmall)`
  a {
    color: ${Colours.blue};
  }
`

const DialogActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`
